import { useRef, useEffect, useState, useCallback } from 'react'
import { cn } from '@/lib/utils'

interface OtpInputProps {
  value: string
  onChange: (value: string) => void
  hasError?: boolean
  disabled?: boolean
}

const OTP_LENGTH = 6

/**
 * Six-slot OTP input backed by a single invisible text input (same pattern as
 * the end-user app): the real input is stretched over the visual slots so
 * paste, autofill (`one-time-code`) and native selection all work.
 */
export const OtpInput = ({ value, onChange, hasError, disabled }: OtpInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [mirrorSelectionStart, setMirrorSelectionStart] = useState<number | null>(null)
  const [mirrorSelectionEnd, setMirrorSelectionEnd] = useState<number | null>(null)

  // Track previous selection for direction detection
  const prevSelectionRef = useRef<{
    start: number | null
    end: number | null
    direction: 'forward' | 'backward' | 'none'
  }>({ start: null, end: null, direction: 'none' })

  // Focus input on mount and when error occurs
  useEffect(() => {
    inputRef.current?.focus()
  }, [hasError])

  // Main selection change handler
  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    const onDocumentSelectionChange = () => {
      if (document.activeElement !== input) {
        setMirrorSelectionStart(null)
        setMirrorSelectionEnd(null)
        return
      }

      const currentStart = input.selectionStart
      const currentEnd = input.selectionEnd
      const currentDirection = input.selectionDirection
      const inputValue = input.value
      const previousSelection = prevSelectionRef.current

      let newStart = -1
      let newEnd = -1
      let newDirection: 'forward' | 'backward' | 'none' = 'none'

      if (inputValue.length !== 0 && currentStart !== null && currentEnd !== null) {
        const isSingleCaret = currentStart === currentEnd
        const isInsertMode = currentStart === inputValue.length && inputValue.length < OTP_LENGTH

        if (isSingleCaret && !isInsertMode) {
          const cursorPosition = currentStart
          if (cursorPosition === 0) {
            // At start: select first character
            newStart = 0
            newEnd = 1
            newDirection = 'forward'
          } else if (cursorPosition === OTP_LENGTH) {
            // At max: select last character
            newStart = cursorPosition - 1
            newEnd = cursorPosition
            newDirection = 'backward'
          } else if (OTP_LENGTH > 1 && inputValue.length > 1) {
            // Middle position: determine direction from previous selection
            let offset = 0
            if (previousSelection.start !== null && previousSelection.end !== null) {
              newDirection = cursorPosition < previousSelection.end ? 'backward' : 'forward'
              const wasPreviouslyInserting =
                previousSelection.start === previousSelection.end &&
                previousSelection.start < OTP_LENGTH
              if (newDirection === 'backward' && !wasPreviouslyInserting) {
                offset = -1
              }
            }
            newStart = offset + cursorPosition
            newEnd = offset + cursorPosition + 1
          }
        }

        // Apply the calculated selection to the input
        if (newStart !== -1 && newEnd !== -1 && newStart !== newEnd) {
          input.setSelectionRange(newStart, newEnd, newDirection)
        }
      }

      // Update mirror state
      const finalStart = newStart !== -1 ? newStart : currentStart
      const finalEnd = newEnd !== -1 ? newEnd : currentEnd
      const finalDirection = newDirection !== 'none' ? newDirection : (currentDirection ?? 'none')

      setMirrorSelectionStart(finalStart)
      setMirrorSelectionEnd(finalEnd)
      prevSelectionRef.current = { start: finalStart, end: finalEnd, direction: finalDirection }
    }

    document.addEventListener('selectionchange', onDocumentSelectionChange, { capture: true })

    // Initial sync
    onDocumentSelectionChange()

    return () => {
      document.removeEventListener('selectionchange', onDocumentSelectionChange, { capture: true })
    }
  }, [])

  // Sync selection state when value or focus changes
  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    // Use setTimeout to ensure this runs after React updates
    const timeoutId = setTimeout(() => {
      const selectionStart = input.selectionStart
      const selectionEnd = input.selectionEnd
      const selectionDirection = input.selectionDirection
      if (selectionStart !== null && selectionEnd !== null) {
        setMirrorSelectionStart(selectionStart)
        setMirrorSelectionEnd(selectionEnd)
        prevSelectionRef.current = {
          start: selectionStart,
          end: selectionEnd,
          direction: selectionDirection ?? 'none',
        }
      }
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [value, isFocused])

  // Block non-digit input before it happens
  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const inputEvent = e.nativeEvent as InputEvent
    // Allow deletion, but block non-digit character insertion
    if (inputEvent.data && !/^\d+$/.test(inputEvent.data)) {
      e.preventDefault()
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH)
    onChange(newValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // If there's an error and user presses backspace, clear entire input
    if (hasError && e.key === 'Backspace') {
      e.preventDefault()
      onChange('')
    }
  }

  const handleFocus = useCallback(() => {
    const input = inputRef.current
    if (!input) return

    // On focus, select from last char position to end
    const start = Math.min(input.value.length, OTP_LENGTH - 1)
    const end = input.value.length
    input.setSelectionRange(start, end)
    setMirrorSelectionStart(start)
    setMirrorSelectionEnd(end)
    setIsFocused(true)
  }, [])

  const handleBlur = () => {
    setIsFocused(false)
    setMirrorSelectionStart(null)
    setMirrorSelectionEnd(null)
  }

  // Compute slot properties
  const getSlotProps = (index: number) => {
    const char = value[index] ?? null
    const isActive =
      isFocused &&
      mirrorSelectionStart !== null &&
      mirrorSelectionEnd !== null &&
      ((mirrorSelectionStart === mirrorSelectionEnd && index === mirrorSelectionStart) ||
        (index >= mirrorSelectionStart && index < mirrorSelectionEnd))

    const hasFakeCaret = isActive && char === null

    return { char, isActive, hasFakeCaret }
  }

  return (
    <div
      ref={containerRef}
      className="relative flex gap-2 sm:gap-3 select-none"
      style={{ cursor: disabled ? 'default' : 'text', pointerEvents: 'none' }}
    >
      {/* Visual slots */}
      {Array.from({ length: OTP_LENGTH }).map((_, index) => {
        const { char, isActive, hasFakeCaret } = getSlotProps(index)

        return (
          <div
            key={index}
            className={cn(
              // 6×w-12 + 5×gap-3 = 348px — must stay under the login card's
              // inner width (440px − 2×p-10 = 360px) or the row gets clipped
              'w-11 h-14 sm:w-12 sm:h-15 flex items-center justify-center',
              'text-xl sm:text-2xl font-medium',
              'border rounded-sm bg-background',
              'transition-all duration-200',
              isActive && 'border-primary shadow-[0px_0px_3px_0px_rgba(22,8,50,0.3)]',
              hasError ? 'border-error' : !isActive ? 'border-border' : '',
              disabled && 'bg-disabled cursor-not-allowed'
            )}
          >
            {char !== null ? (
              <span className="text-primary">{char}</span>
            ) : hasFakeCaret ? (
              <span className="w-0.5 h-5 bg-foreground animate-caret-blink" />
            ) : (
              <span className="text-muted">*</span>
            )}
          </div>
        )
      })}

      {/* Actual input - positioned over slots, transparent but interactive */}
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        data-input-otp
        value={value}
        onBeforeInput={handleBeforeInput}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        className="absolute inset-0 w-full h-full bg-transparent text-transparent"
        style={{
          caretColor: 'transparent',
          pointerEvents: 'all',
          outline: 'none',
          border: 'none',
          fontSize: 'inherit',
          letterSpacing: '-0.5em',
          fontFamily: 'monospace',
        }}
      />
    </div>
  )
}
